// Consents tab (design/screens/patient-record.html "Consents"; UC-GOVERNANCE-015, UC-GOVERNANCE-014): every consent record of
// the patient, newest first — version, status, channel, signer, who captured it and each purpose's answer with who changed it
// and why — with the signature opened through the documents route (which logs the open), and Withdraw on the latest record:
// the withdrawable purposes still granted, a reason, `POST …/consents/{consentId}/withdraw` (R-15; never care and records).
// A role holding `governance.consent.write` records a consent at the desk with Record consent… (`?record=desk` — the header's
// consent alert and Today's check-in prompt link there): the desk capture of PatientsConsentDesk.tsx (UC-GOVERNANCE-013 A3,
// BR-GOVERNANCE-026). A scanned paper form opens like the signature, as "Open signed form".
import { CONSENT_PURPOSE_DEFINITIONS, CONSENT_PURPOSE_LABELS, controlIdDisplay, type ConsentCapturePlace, type ConsentChannel, type ConsentPurpose, type ConsentRecordView, type PatientView } from "@visionx/contracts";
import { useQuery, useQueryClient } from "@tanstack/react-query";
import { useState, type ReactNode } from "react";
import { useSearchParams } from "react-router";
import { consent as consentApi } from "../api/endpoints.consent";
import { patientDocuments } from "../api/endpoints.patients";
import { describeError, isApiError } from "../api/problem";
import { formatDate, formatDateTime } from "../lib/format";
import { can, useSessionView } from "../session/store";
import { WRITE_BLOCK_TITLE, useWriteBlock } from "../shell/OfflineBanner";
import { Alert, Checkbox, Chip, Empty, ErrorAlert, Field, Loading, Modal, Pager, Panel, TextArea } from "../ui/bits";
import { Icon } from "../ui/icons";
import { ConsentDeskCapture } from "./PatientsConsentDesk";

const CHANNEL_LABEL: Record<Exclude<ConsentChannel, "InStoreTablet">, string> = { Online: "online", Paper: "on paper", PaperScan: "on paper, scanned", Verbal: "verbally" };
/**
 * How and where a record was signed, in words (UC-GOVERNANCE-015 step 3; BR-GOVERNANCE-020): `InStoreTablet` is both the kiosk
 * and the desk's pad, so a record signed on a screen is named by where it was captured — "on the kiosk" or "on the desk
 * screen" (an older answer that does not say reads "on a screen"). Shared with the re-consent report's Last signed column.
 */
export function consentSignedHow(channel: ConsentChannel, capturedOn: ConsentCapturePlace | null): string {
  if (channel !== "InStoreTablet") return CHANNEL_LABEL[channel];
  return capturedOn === "Kiosk" ? "on the kiosk" : capturedOn === "Desk" ? "on the desk screen" : "on a screen";
}
/** How a consent recorded at the desk was given, for the notice after it is recorded (BR-GOVERNANCE-026). */
const RECORDED_HOW: Record<ConsentChannel, string> = {
  InStoreTablet: "signed on the screen", Online: "online", Paper: "on a signed paper form filed at the practice", PaperScan: "on a signed paper form, scanned", Verbal: "given verbally",
};
/** The address parameter that opens the desk capture on this tab (`/patients/{id}/consents?record=desk`). */
export const DESK_CAPTURE_PARAM = "record";
export const deskCapturePath = (patientId: string): string => `/patients/${patientId}/consents?${DESK_CAPTURE_PARAM}=desk`;
const STATUS_TONE: Record<ConsentRecordView["status"], "ok" | "warn" | "crit"> = { Current: "ok", Outdated: "warn", Withdrawn: "crit" };
const WITHDRAWABLE = new Set(CONSENT_PURPOSE_DEFINITIONS.filter((d) => d.withdrawable).map((d) => d.purpose));
const who = (u: { displayName: string; controlId: number } | null): string => (u ? `${u.displayName} ${controlIdDisplay(u.controlId)}` : "—");

/** The withdrawable purposes the record still grants on some channel (UC-GOVERNANCE-014 step 2). */
const grantedWithdrawable = (r: ConsentRecordView): ConsentPurpose[] => [...new Set(r.purposes.filter((p) => p.granted && WITHDRAWABLE.has(p.purpose)).map((p) => p.purpose))];

export function PatientsConsents({ p }: { p: PatientView }) {
  const sess = useSessionView();
  const [cursors, setCursors] = useState<string[]>([]);
  const cursor = cursors[cursors.length - 1];
  const q = useQuery({ queryKey: ["patient", p.id, "consents", cursor ?? ""], queryFn: () => consentApi.history(p.id, { cursor }) });
  const [withdrawing, setWithdrawing] = useState<ConsentRecordView | null>(null);
  const [notice, setNotice] = useState<ReactNode | null>(null);
  const block = useWriteBlock();
  const [params, setParams] = useSearchParams();
  const rows = q.data?.data.items ?? [];
  const canWithdraw = can(sess, "governance.consent.write");
  const canCapture = canWithdraw;
  const desk = canCapture && params.get(DESK_CAPTURE_PARAM) === "desk";
  const setDesk = (on: boolean) => {
    const next = new URLSearchParams(params);
    if (on) next.set(DESK_CAPTURE_PARAM, "desk"); else next.delete(DESK_CAPTURE_PARAM);
    setParams(next, { replace: !on });
  };
  if (desk) {
    return (
      <ConsentDeskCapture p={p} onCancel={() => setDesk(false)} onDone={(r) => {
        setDesk(false);
        setCursors([]);
        setNotice(<Alert tone="ok"><b>Consent recorded on version {r.textVersion}</b> — {RECORDED_HOW[r.channel]}, captured by {who(r.capturedBy)}.</Alert>);
      }} />
    );
  }
  return (
    <Panel title="Consent records" meta="newest first · every signing is kept" tight
      head={canCapture ? <button className="btn sm right" type="button" disabled={block !== null} title={block ? WRITE_BLOCK_TITLE[block] : "Record a consent given at the desk — on the screen, on paper or verbally"} onClick={() => { setNotice(null); setDesk(true); }}><Icon name="plus" />Record consent…</button> : undefined}
      foot={<>
      <span className="help">A withdrawal stops reminders, marketing, sharing and the CRM push from now on; invoices and statements still go out (R-15). Care and records is withdrawn only by an objection or erasure request.</span>
      {q.data && (cursors.length > 0 || q.data.data.nextCursor) && <Pager label={`Page ${cursors.length + 1}`} hasPrev={cursors.length > 0} hasNext={!!q.data.data.nextCursor} onPrev={() => setCursors((c) => c.slice(0, -1))} onNext={() => setCursors((c) => [...c, q.data!.data.nextCursor!])} />}
    </>}>
      {notice && <div className="panel-body">{notice}</div>}
      {q.isPending && <div className="panel-body"><Loading what="consent records" /></div>}
      {q.isError && <div className="panel-body"><ErrorAlert error={q.error} /></div>}
      {q.data && rows.length === 0 && <Empty title="No consent on record.">{canCapture ? "Record the patient's first signing with Record consent… at the desk, or hand the kiosk over with Re-consent on kiosk." : "Hand the kiosk over with Re-consent on kiosk to record the patient's first signing."}</Empty>}
      {rows.length > 0 && (
        <div className="list">
          {rows.map((r) => {
            const open = grantedWithdrawable(r);
            return (
              <div key={r.id} className="item consent-record">
                <div className="grow stack gap-4">
                  {/* Only the latest record governs (BR-GOVERNANCE-021): an earlier one reads "Superseded" alone, never its own status beside it. */}
                  <div className="primary">Version {r.textVersion} {r.isLatest ? <Chip tone={STATUS_TONE[r.status]} dot>{r.status}</Chip> : <Chip tone="neutral">Superseded</Chip>}</div>
                  <div className="sub">
                    Signed {formatDateTime(r.signedAt)} {consentSignedHow(r.channel, r.capturedOn)}{r.branch ? ` at ${r.branch.code}` : ""} by {r.signer.name ?? "—"}{r.signer.type === "Guardian" ? ` (${r.signer.guardian?.relationship.toLowerCase() ?? "guardian"} of the patient)` : ""} · captured by {who(r.capturedBy)}
                  </div>
                  <table>
                    <tbody>
                      {r.purposes.map((x) => (
                        <tr key={`${x.purpose}-${x.channel ?? ""}`}>
                          <td>{CONSENT_PURPOSE_LABELS[x.purpose]}{x.channel ? ` · ${x.channel}` : ""}</td>
                          <td>{x.granted ? <Chip tone="ok">Agreed</Chip> : <Chip tone="neutral">Not agreed</Chip>}</td>
                          <td className="small faint">{x.changedBy ? `changed ${formatDate(x.changedAt)} by ${who(x.changedBy)}${x.reason ? ` — ${x.reason}` : ""}` : ""}</td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                  {r.withdrawnAt && <div className="small">Withdrawn {formatDateTime(r.withdrawnAt)} by {who(r.withdrawnBy)}{r.withdrawalReason ? ` — ${r.withdrawalReason}` : ""}</div>}
                </div>
                <div className="stack gap-4">
                  {r.signatureDocument && <a className="btn sm" href={patientDocuments.urlPath(p.id, r.signatureDocument.id)} target="_blank" rel="noopener">Open signature</a>}
                  {r.formDocument && <a className="btn sm" href={patientDocuments.urlPath(p.id, r.formDocument.id)} target="_blank" rel="noopener">Open signed form</a>}
                  {r.isLatest && canWithdraw && open.length > 0 && <button className="btn sm" type="button" disabled={block !== null} title={block ? WRITE_BLOCK_TITLE[block] : undefined} onClick={() => { setNotice(null); setWithdrawing(r); }}>Withdraw…</button>}
                </div>
              </div>
            );
          })}
        </div>
      )}
      {withdrawing && <WithdrawModal p={p} record={withdrawing} onClose={() => setWithdrawing(null)} onDone={(n) => { setWithdrawing(null); setNotice(n); }} />}
    </Panel>
  );
}

/** UC-GOVERNANCE-014 steps 2–6: the withdrawable purposes still granted, a reason, and the answer's record. */
function WithdrawModal({ p, record, onClose, onDone }: { p: PatientView; record: ConsentRecordView; onClose: () => void; onDone: (n: ReactNode) => void }) {
  const qc = useQueryClient();
  const offered = grantedWithdrawable(record);
  const [chosen, setChosen] = useState<Set<ConsentPurpose>>(new Set());
  const [reason, setReason] = useState("");
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<unknown>(null);
  const ready = chosen.size > 0 && reason.trim() !== "" && !busy;
  const submit = async () => {
    if (!ready) return;
    setBusy(true);
    setError(null);
    try {
      const { data } = await consentApi.withdraw(p.id, record.id, { purposes: [...chosen], reason: reason.trim() });
      void qc.invalidateQueries({ queryKey: ["patient", p.id] });
      void qc.invalidateQueries({ queryKey: ["patients"] });
      onDone(<Alert tone="ok"><b>Withdrawn: {[...chosen].map((x) => CONSENT_PURPOSE_LABELS[x]).join(", ")}.</b> {data.status === "Withdrawn" ? "Only care and records remain; a new signing is the way back." : "The other answers are unchanged."}</Alert>);
    } catch (err) {
      setError(err);
    } finally {
      setBusy(false);
    }
  };
  return (
    <Modal title={`Withdraw consent · version ${record.textVersion}`} meta={`signed ${formatDateTime(record.signedAt)}`} onClose={onClose} onSubmit={() => void submit()} footer={<>
      <button className="btn" type="button" onClick={onClose}>Cancel</button>
      <button className="btn primary" type="submit" disabled={!ready}>{busy ? "Withdrawing…" : "Withdraw"}</button>
    </>}>
      {error !== null && <Alert tone="crit"><b>Could not withdraw.</b> {isApiError(error) && error.code === "ILLEGAL_TRANSITION" ? "A newer consent record exists; withdraw on that one." : describeError(error)}</Alert>}
      <fieldset className="stack gap-8">
        <legend className="small strong muted">What the patient no longer agrees to</legend>
        {offered.map((x) => (
          <Checkbox key={x} name={x} checked={chosen.has(x)} onChange={(on) => setChosen((c) => { const n = new Set(c); if (on) n.add(x); else n.delete(x); return n; })}>{CONSENT_PURPOSE_LABELS[x]}</Checkbox>
        ))}
      </fieldset>
      <Field label="Reason" required htmlFor="consent-withdraw-reason" help="Kept with each withdrawn answer, with you and the time"><TextArea id="consent-withdraw-reason" value={reason} maxLength={500} rows={2} placeholder="Patient's email of 12 Sep 2026" onChange={(e) => setReason(e.target.value)} /></Field>
    </Modal>
  );
}
